import { createApi } from '@reduxjs/toolkit/query/react';
import { createSigV4BaseQuery } from './sigv4BaseQuery';
import { config } from '@config/index';

// NOTE: The base query is created lazily via a thunk so that `useAuth` is not
// called at module-evaluation time (it requires a React context).  Instead,
// the getCredentials function is wired up when the store is configured.
//
// For now we create the base query with a direct reference to the auth
// context's getCredentials.  The store setup will provide the getter.

let _getCredentials: (() => Promise<import('@auth/types').AWSCredentials>) | null = null;

/**
 * Wire the auth credential getter into the API layer.
 * Called once during app initialisation from the store or App component.
 */
export function setApiCredentialsGetter(
  getter: () => Promise<import('@auth/types').AWSCredentials>,
) {
  _getCredentials = getter;
}

const sigv4BaseQuery = createSigV4BaseQuery(
  async () => {
    if (!_getCredentials) {
      throw new Error(
        'API credentials getter not initialised. Call setApiCredentialsGetter() before making API requests.',
      );
    }
    return _getCredentials();
  },
  config.apiEndpoint,
  config.region,
);

/**
 * Central RTK Query API slice.
 * Endpoints are injected from separate files via `baseApi.injectEndpoints`.
 */
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: sigv4BaseQuery,
  tagTypes: [
    'SurveyQuestions',
    'SurveyTemplates',
    'TemplateQuestions',
    'SurveySchedules',
    'SurveyInstances',
    'SurveyAnalytics',
    'PatientPreference',
    'TemplateTestStatus',
    'MessengerOptions',
    'PracticeMessenger',
    'EmailSenderConfig',
    'LinkDomain',
    'AdpFields',
    'ReviewPlatforms',
    'ReviewSiteConfig',
    'ScheduleJobs',
    'RunnerConfig',
    'GlobalTemplates',
    'GlobalQuestions',
    'PendingPractices',
    'Roles',
    'FieldMappings',
    'DerivedFields',
    'PmsMappings',
    'PracticeAdpFields',
    'SchedulerSettings',
    'DataLoadThresholds',
    'ReportConfig',
    'ReportSchedulerSettings',
    'VisitAnalytics',
    'PracticeVisitConfig',
    'VisitReportConfig',
    'VisitReportSchedulerSettings',
    'AlarmDailyReportSchedulerSettings',
    'VisitAlarmDigestSettings',
    'ClassMappingDefinitions',
    'ClassMappings',
    'PracticeMembers',
    'DistinctProviders',
    'ProviderReportSchedulerSettings',
    'HalfDayMarkings',
    'ExcludedDays',
    'Metrics',
    'MetricValues',
    'MetricOverrides',
    'MetricsSchedule',
    'FilterValues',
    'DataLoads',
    'ComputedMetrics',
    'ComputedMetricValues',
    'Alarms',
    'AlarmState',
    'Run',
    'RunList',
    'SystemEvent',
    'SystemEventList',
    'MetricGroups',
    'Worklist',
    'WorklistItem',
    'WorklistOpenCounts',
    'WorklistEmailConfig',
    'GoalSet',
    'GoalSetTree',
    'DataLoadDisplayColumns',
    'Reminders',
    'Reminder',
    'Groups',
    'AccessControl',
    'MyAccess',
    'DefaultPolicyTemplate',
    'VisitAlarmRules',
    'VisitAlarmResults',
    'VisitAlarmRecipients',
    'VisitWeights',
    'ScheduledHalfDay',
    'ScheduledExcludedDays',
    // Work_Tracking (workTrackingApi): Departments, Issues/Risks/Ideas + their
    // per-{type,id} detail, polymorphic Tasks, and the reverse Entity_Backlink
    // index keyed per {dataLoadId, sourceRowKey}. See design § Frontend.
    'Department',
    'DepartmentDetail',
    'Issue',
    'Risk',
    'Idea',
    'Question',
    'Opportunity',
    'WorkItemDetail',
    'WorkItemTask',
    'Backlink',
    // HR_Module (hrApi): the non-compensation surface — HrResource (list +
    // per-id), the per-resource schedule versions, both hierarchy views, the
    // Cost_Rollup, the Capacity_Consumer contract, the provider-class
    // consistency read, and the HR practice configuration.
    //
    // The Compensation_Endpoint is deliberately NOT in this group: it lands
    // under its own distinct tag so a resource-list refetch never pulls a
    // compensation object into the store, and invalidations never cross between
    // the two families (Req 18.3, 18.4, design § Design decisions D-3).
    'HrResource',
    'HrScheduleVersion',
    'HrHierarchy',
    'HrCostRollup',
    'HrCapacityContract',
    'HrProviderClassConsistency',
    'HrConfig',
    // HR_Module compensation (hrCompensationApi): the Compensation_Endpoint's
    // own tag family, registered here because RTK Query requires every tag type
    // up front — and listed apart from the seven above because that is the whole
    // point. `HrCompensation` covers the versions and the per-resource
    // Allocated_Cost; `HrCompensationAudit` covers the append-only trail.
    //
    // Nothing in `hrApi.ts` provides or invalidates either, and nothing in
    // `hrCompensationApi.ts` provides or invalidates any of the seven above. A
    // resource-list refetch therefore cannot pull a compensation object into the
    // store, and only `CompensationPanel` subscribes to these two
    // (Req 18.3, 18.4, 18.6, design § Design decisions D-3).
    'HrCompensation',
    'HrCompensationAudit',
    // Billing_Collections_Module (billingApi): one tag type for both analytics
    // reads, carrying a `SUMMARY` / `DETAIL` id so the dashboard card and the
    // analytics page cache and refetch independently — the same shape
    // `VisitAnalytics` uses for its sibling pair.
    'BillingAnalytics',
  ],
  endpoints: () => ({}),
});
